import React, { useRef, useState } from 'react';
import { Play, Pause, FileText, Mic, Sparkles, Calendar, User, Building2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';

const TaskDetailDialog = ({ open, onOpenChange, task, companyName }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  if (!task) return null;

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const isCompleted = task.status === 'Completed';
  const hasCompletionInfo = task.completion_notes || task.completion_audio_url || task.completion_transcript || task.completion_summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <DialogTitle className="text-xl">{task.title}</DialogTitle>
            <Badge variant={isCompleted ? 'default' : 'secondary'} className={isCompleted ? 'bg-green-500' : ''}>
              {isCompleted ? 'Completada' : 'Pendiente'}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Task Info */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Información de la Tarea</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {task.due_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <span className="text-muted-foreground">Fecha límite:</span>
                    <p className="font-medium">{task.due_date}</p>
                  </div>
                </div>
              )}
              {companyName && (
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <span className="text-muted-foreground">Empresa:</span>
                    <p className="font-medium">{companyName}</p>
                  </div>
                </div>
              )}
              {task.owner && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <span className="text-muted-foreground">Responsable:</span>
                    <p className="font-medium">{task.owner}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Completion Info - Only show if task is completed */}
          {isCompleted && hasCompletionInfo && (
            <>
              <Separator />
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-green-600 uppercase tracking-wide flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Registro de Completado
                </h3>

                {/* Completion metadata */}
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  {task.completed_by && (
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span>Completada por: <strong>{task.completed_by}</strong></span>
                    </div>
                  )}
                  {task.completed_at && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(task.completed_at)}</span>
                    </div>
                  )}
                </div>

                {/* AI Summary */}
                {task.completion_summary && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2 text-green-600">
                      <Sparkles className="w-4 h-4" />
                      Resumen (IA)
                    </label>
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-sm">
                      {task.completion_summary}
                    </div>
                  </div>
                )}

                {/* Written Notes */}
                {task.completion_notes && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Notas Escritas
                    </label>
                    <div className="bg-muted/50 rounded-lg p-4 text-sm whitespace-pre-wrap">
                      {task.completion_notes}
                    </div>
                  </div>
                )}

                {/* Audio Recording */}
                {task.completion_audio_url && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Mic className="w-4 h-4" />
                      Nota de Voz
                    </label>
                    <div className="flex items-center gap-3 bg-muted/50 rounded-lg p-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={togglePlayback}
                        data-testid="play-completion-audio"
                      >
                        {isPlaying ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {isPlaying ? 'Reproduciendo...' : 'Reproducir grabación'}
                      </span>
                      <audio
                        ref={audioRef}
                        src={task.completion_audio_url}
                        onEnded={() => setIsPlaying(false)}
                        className="hidden"
                      />
                    </div>
                  </div>
                )}

                {/* Transcript */}
                {task.completion_transcript && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2 text-blue-600">
                      <FileText className="w-4 h-4" />
                      Transcripción (IA)
                    </label>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm">
                      {task.completion_transcript}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* No completion info message */}
          {isCompleted && !hasCompletionInfo && (
            <>
              <Separator />
              <div className="text-center py-4 text-sm text-muted-foreground">
                <p>Esta tarea fue completada sin registro de notas.</p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailDialog;
